import { useCallback, useContext, useLayoutEffect } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5hierarchy from '@amcharts/amcharts5/hierarchy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { FaArrowCircleLeft } from "react-icons/fa";
import { ISCOArchetypeContext } from '@providers/ISCOArchetype';
import ISCODetails from './ISCODetails';
import { DefaultAvatarsClassification } from '@config/avatars-classification';

import './ChildISCOChart.scss';
import SkillsDetails from './SkillsDetails';

type ChildISCOChartProps = {
    handleGoBackToParentISCOChart: () => void;
    countryCode: string;
};

const ChildISCOChart = ({ handleGoBackToParentISCOChart, countryCode }: ChildISCOChartProps) => {
    const { selectedParentISCO, showISCODetails, setShowISCODetails, showSkillsDetails, setShowSkillsDetails, setSelectedChildISCO } = useContext(ISCOArchetypeContext);

    const chartId = "child-isco-chart" + Date.now().toString().substr(-4);

    useLayoutEffect(() => {
        if (!showISCODetails && !showSkillsDetails && selectedParentISCO) {
            am5.ready(function () {
                am5.addLicense('AM5M326788873');

                // Create root and chart
                var root = am5.Root.new(chartId);

                // Set slice color from the following list
                const defaultTheme = am5.Theme.new(root);
                root.setThemes([am5themes_Animated.new(root), defaultTheme]);

                // Enable zooming and panning on the chart
                root.container.set("interactive", true);
                root.container.set("wheelable", true);

                defaultTheme.rule("ColorSet").set("colors", [
                    am5.color(0x003b5c),
                    am5.color(0x035684),
                    am5.color(0x036eaa),
                    am5.color(0x0486cf),
                    am5.color(0x00293f),
                    am5.color(0x118ab2),
                    am5.color(0x253948),
                    am5.color(0x385870),
                    am5.color(0x487292),
                    am5.color(0x06d6a0),
                    am5.color(0x5686aa),
                    am5.color(0x073b4c),
                    am5.color(0x71afde),
                ]);

                var container = root.container.children.push(
                    am5.Container.new(root, {
                        width: am5.percent(100),
                        height: am5.percent(100),
                        layout: root.verticalLayout
                    })
                );

                const maxRadius = 200; // Set your desired maximum radius
                const minRadius = 50; // Set your minimum radius

                var series = container.children.push(
                    am5hierarchy.ForceDirected.new(root, {
                        singleBranchOnly: false,
                        downDepth: 2,
                        topDepth: 1,
                        initialDepth: 0,
                        valueField: "isco4_job_count_modified",
                        categoryField: "isco4_label",
                        childDataField: "children",
                        xField: "x",
                        yField: "y",
                        minRadius: minRadius,
                        maxRadius: maxRadius,
                        interactive: true,
                        manyBodyStrength: -65
                    })
                );

                series.labels.template.set("forceHidden", true);
                series.outerCircles.template.set("forceHidden", true);
                series.circles.template.set("forceHidden", true);


                // Define data
                let childrenData = {
                    value: 0,
                    children: selectedParentISCO.childrenISCO4
                };

                // Find the maximum value in the data
                const maxCircleValue = 1200;

                series.nodes.template.setup = function (target: am5hierarchy.LinkedHierarchyNode) {
                    let label: any = null; // Declare label variable

                    target.events.on("dataitemchanged", function (ev: any) {
                        const dataItem = ev.target.dataItem;
                        const value = dataItem.dataContext.isco4_job_count_modified; // Get the value
                        const radius = minRadius + ((value / maxCircleValue) * (maxRadius - minRadius)); // Calculate the radius

                        // Clear previous shapes and icons if they exist
                        target.children.each(function (child: any) {
                            if (child instanceof am5.Container) {
                                child.dispose(); // Dispose of the previous container holding circle and picture
                            }
                        });

                        // Create a new container for the circle and picture
                        let container = target.children.push(am5.Container.new(root, {}));

                        // Create a circular white background
                        container.children.push(am5.Circle.new(root, {
                            radius: radius,
                            fill: am5.color(0xffffff), // White circle background
                            stroke: dataItem.dataContext.isco4_impact === "Shrinking" ? am5.color(0xf7aba3)
                                : dataItem.dataContext.isco4_impact === "Growing" ? am5.color(0xdbff9e)
                                    : dataItem.dataContext.isco4_impact === "Stable" ? am5.color(0xfcd695)
                                        : am5.color(0x043c5b),
                            strokeWidth: 8,
                            centerX: am5.percent(50), // Center the circle
                            centerY: am5.percent(50),  // Center the circle
                        }));

                        // Create and position the background image
                        container.children.push(am5.Picture.new(root, {
                            width: am5.percent(97),       // Make the image fill the container
                            height: am5.percent(97),      // Make the image fill the container
                            centerX: am5.percent(50),      // Center the image
                            centerY: am5.percent(50),      // Center the image
                            src: process.env.REACT_APP_CHAT_ISCO_ARCHETYPE_URL + DefaultAvatarsClassification[countryCode].theme + "/" + DefaultAvatarsClassification[countryCode].region + "/" + DefaultAvatarsClassification[countryCode].ethnicity + "/" + dataItem.dataContext.isco4_code + ".png",
                        }));

                        // Dispose of the old label if it exists
                        if (label) {
                            label.dispose();
                        }

                        const shouldHideTooltipAndLabel = dataItem.dataContext.isco4_label === "Skill" || dataItem.dataContext.isco4_label === "Profile"; // Add a custom property in data
                        if (!shouldHideTooltipAndLabel) {
                            // Create the new label and position it below the circle
                            label = am5.Label.new(root, {
                                text: dataItem.dataContext.isco4_label,
                                fill: am5.color(0x043c5b),
                                fontSize: 14,
                                y: radius + 20, // Set vertical space below the circle
                                zIndex: 100, // Set the zIndex to a high value to ensure it's on top
                                oversizedBehavior: "truncate",
                                maxWidth: 250,
                                height: 30,
                                // Add padding to make room for the rounded corners
                                paddingLeft: 10,
                                paddingRight: 10,
                                paddingTop: 7,
                                paddingBottom: 5,
                                background: am5.RoundedRectangle.new(root, {
                                    fill: am5.color(0xffffff),
                                })
                            });

                            // Add the label after the container to ensure it's on top
                            target.children.push(label);
                        }

                        if (shouldHideTooltipAndLabel) {
                            // Set cursor to pointer for "Skill" and "Profile" nodes
                            target.set("cursorOverStyle", "pointer");
                        }

                        // Customize tooltip visibility for the nodes of skill and profile
                        target.adapters.add('tooltipText', (value: string | undefined, target: any) => {
                            return shouldHideTooltipAndLabel ? undefined : (target.dataItem.dataContext.isco4_label + ": " + target.dataItem.dataContext.isco4_job_count);
                        });

                        // Add label inside the circle just for Skill and Profile
                        if (shouldHideTooltipAndLabel) {
                            container.children.push(am5.Label.new(root, {
                                text: dataItem.dataContext.isco4_label,
                                fontSize: 12,
                                fill: am5.color(0x043c5b),
                                centerX: am5.percent(50),
                                centerY: am5.percent(50),
                                textAlign: "center"
                            }));
                        }
                    });

                    // Add click event listener to the target if it's a Profile target
                    target.events.on("click", function (currentTarget: any) {
                        setSelectedChildISCO && setSelectedChildISCO(currentTarget.target.dataItem.get("parent").dataContext); // Set the selected item
                        if (currentTarget.target.dataItem.dataContext.isco4_label === "Profile") {
                            setShowISCODetails && setShowISCODetails(true);
                        }
                        else {
                            if (currentTarget.target.dataItem.dataContext.isco4_label === "Skill") {
                                setShowSkillsDetails && setShowSkillsDetails(true);
                            }
                        }
                    });
                };

                series.data.setAll([childrenData]);

                // Enable zooming with mouse wheel on the entire chart
                const handleWheel = (event: any) => {
                    event.originalEvent.preventDefault(); // Prevent default scrolling

                    // Determine zoom factor
                    const zoomFactor = event.originalEvent.deltaY > 0 ? 1.1 : 0.9; // Zoom out or in

                    // Get the current scale
                    const scale = series.get("scale") || 1;

                    // Update scale on the series
                    series.set("scale", scale * zoomFactor);
                };

                // Add wheel event listener to the root container
                root.container.events.on("wheel", handleWheel);

                series.set("selectedDataItem", series.dataItems[0]);
            });
        }
    }, [showISCODetails, selectedParentISCO, setSelectedChildISCO, setShowISCODetails, showSkillsDetails, setShowSkillsDetails]); // eslint-disable-line

    const handleGoBackToChildISCOChart = useCallback(() => {
        setShowISCODetails && setShowISCODetails(false);
        setShowSkillsDetails && setShowSkillsDetails(false);
        setSelectedChildISCO && setSelectedChildISCO(null);
    }, [setSelectedChildISCO, setShowISCODetails]);

    return <>
        {!showISCODetails && !showSkillsDetails && selectedParentISCO ?
            <>
                <div onClick={handleGoBackToParentISCOChart} className="child-isco-chart-back-button-container">
                    <FaArrowCircleLeft className="child-isco-chart-back-button-arrow" />
                    <div className="child-isco-chart-back-button">{selectedParentISCO.isco3_label}</div>
                </div>
                <div id={chartId} className="child-isco-chart-container"></div>
            </>
            :
            <>
                {showISCODetails && <ISCODetails handleGoBackToChildISCOChart={handleGoBackToChildISCOChart} countryCode={countryCode} />}
                {showSkillsDetails && <SkillsDetails handleGoBackToChildISCOChart={handleGoBackToChildISCOChart} countryCode={countryCode} />}
            </>
        }

    </>
};

export default ChildISCOChart;
